/**
 * Phase 76.2 — Rule-based Season Report draft summary + highlights generators.
 *
 * Produces professional-sounding coaching language from the structured
 * data already loaded in the Builder. Zero external/AI calls — strictly
 * deterministic templates so the same input always yields the same text.
 *
 * Each generator returns a string the staff can then edit in a textarea
 * before saving. If there is no structured data for a section, an empty
 * string is returned so the Builder can show a placeholder hint.
 */

const fmt = (n, suffix = '') =>
  (n === null || n === undefined || Number.isNaN(n)) ? '—' : `${n}${suffix}`;
const delta = (a, b, suffix = '') => {
  if (a === null || a === undefined || b === null || b === undefined) return null;
  const d = Number(b) - Number(a);
  if (!Number.isFinite(d)) return null;
  const sign = d > 0 ? '+' : '';
  return `${sign}${Number(d.toFixed(2))}${suffix}`;
};

export function generateFitnessSummary(fa) {
  if (!fa || !fa.sessions_count) return '';
  const parts = [];
  parts.push(`Across the season the athlete completed ${fa.sessions_count} fitness assessment session(s).`);
  const improved = (fa.key_changes || []).filter(k => k.delta !== null && k.delta !== undefined && k.delta > 0);
  if (improved.length) {
    const top = improved.slice(0, 3).map(k => `${k.test_type} (${delta(k.first_value, k.latest_value)})`).join(', ');
    parts.push(`Notable improvements: ${top}.`);
  }
  if (fa.needs_attention?.length) {
    parts.push(`Areas requiring attention: ${fa.needs_attention.map(n => n.test_type).join(', ')}.`);
  }
  return parts.join(' ');
}

export function generateBiometricsSummary(bm) {
  if (!bm || !bm.entries_count) return '';
  const parts = [];
  parts.push(`The athlete recorded ${bm.entries_count} biometric entries during the season.`);
  const w = bm.deltas?.weight_kg, h = bm.deltas?.height_cm;
  if (w !== null && w !== undefined) parts.push(`Weight change: ${w > 0 ? '+' : ''}${w} kg.`);
  if (h !== null && h !== undefined) parts.push(`Height change: ${h > 0 ? '+' : ''}${h} cm.`);
  const fat = bm.deltas?.fat_mass_pct;
  if (fat !== null && fat !== undefined) parts.push(`Body fat ${fat > 0 ? 'increased' : 'reduced'} by ${Math.abs(fat)} %.`);
  if (bm.latest_notes) parts.push(`Staff observation: ${bm.latest_notes}.`);
  return parts.join(' ');
}

export function generateNutritionSummary(n) {
  if (!n || (!n.active_plan && !n.older_plans?.length)) return '';
  const parts = [];
  const ap = n.active_plan;
  if (ap) {
    parts.push(`Active nutrition plan: "${ap.title}" (${ap.plan_type || 'general'}).`);
    if (ap.description) parts.push(ap.description);
    if (ap.coaching_notes) parts.push(`Nutritionist notes: ${ap.coaching_notes}.`);
  }
  if (n.older_plans?.length) {
    parts.push(`Previous plans in season: ${n.older_plans.map(p => p.title).join(', ')}.`);
  }
  return parts.join(' ');
}

export function generateMentalSummary(m) {
  if (!m || (!m.entries_count && !m.responses_count)) return '';
  const parts = [];
  parts.push(`Mental development engagement: ${m.entries_count} entries assigned, ${m.responses_count} athlete responses logged, ${m.completed_tasks} task(s) marked complete.`);
  if (m.themes?.length) {
    parts.push(`Development themes covered: ${m.themes.map(t => `${t.label} (${t.count})`).join(', ')}.`);
  }
  parts.push('Detailed private notes are kept confidential and are not included in this report.');
  return parts.join(' ');
}

export function generateAcademicSummary(ac) {
  if (!ac || (!ac.profiles?.length && !ac.reports_count && !(ac.attendance?.weeks))) return '';
  const parts = [];
  if (ac.profiles?.length) {
    parts.push(`Active academic programme(s): ${ac.profiles.map(p => p.profile_name).filter(Boolean).join(', ')}.`);
  }
  const att = ac.attendance || {};
  if (att.rate !== null && att.rate !== undefined) {
    parts.push(`Academic attendance: ${att.rate}% across ${att.weeks} tracked week(s).`);
  }
  const lr = ac.latest_report;
  if (lr) {
    const bits = [];
    if (lr.average_grade) bits.push(`average grade ${lr.average_grade}`);
    if (lr.average_course_completion_percentage !== null && lr.average_course_completion_percentage !== undefined) {
      bits.push(`${lr.average_course_completion_percentage}% completion`);
    }
    parts.push(`Most recent report (${lr.period || 'recent period'}) shows ${bits.join(' / ') || 'progress recorded'}.`);
    if (lr.teacher_comment) parts.push(`Teacher: ${lr.teacher_comment}.`);
  }
  return parts.join(' ');
}

export function generateInjurySummary(i) {
  if (!i || !i.injuries_count) return '';
  const parts = [];
  parts.push(`${i.injuries_count} injury record(s) during the season; ${i.active_count} currently active.`);
  const top = (i.items || []).slice(0, 3);
  if (top.length) {
    parts.push(`Most recent: ${top.map(t => `${t.injury_type || 'injury'} (${t.body_area || '—'}, ${t.status || 'status pending'})`).join('; ')}.`);
  }
  return parts.join(' ');
}

export function generateCareerSummary(c) {
  if (!c || !c.phases_count) return '';
  const parts = [];
  const cp = c.current_phase;
  if (cp) {
    parts.push(`Current pathway phase: "${cp.phase_name}" (${cp.completion_percentage ?? 0}% progress).`);
  }
  if (c.completed_milestones?.length) {
    parts.push(`Milestones achieved this season: ${c.completed_milestones.map(m => m.title).join(', ')}.`);
  }
  if (c.next_milestone) {
    parts.push(`Next step: ${c.next_milestone.title}${c.next_milestone.target_date ? ` (target ${c.next_milestone.target_date})` : ''}.`);
  }
  return parts.join(' ');
}

/**
 * Phase 76.2 Part 3 — Season Highlights generator.
 * Walks across the report payload and synthesises 3–10 short factual
 * bullet points. Staff can edit / reorder / delete each highlight.
 */
export function generateSeasonHighlights(reportData) {
  if (!reportData) return [];
  const out = [];
  const perf = reportData.performance || {};
  const att = reportData.attendance || {};
  const ext = reportData.extended || {};

  // Football performance highlights
  if (perf.total_matches) {
    const ratingBit = perf.avg_rating ? ` (avg rating ${Number(perf.avg_rating).toFixed(1)}/10)` : '';
    out.push(`Played ${perf.total_matches} match${perf.total_matches === 1 ? '' : 'es'}${ratingBit}.`);
  }
  if (perf.goals) out.push(`Scored ${perf.goals} goal${perf.goals === 1 ? '' : 's'}.`);
  if (perf.assists) out.push(`Recorded ${perf.assists} assist${perf.assists === 1 ? '' : 's'}.`);

  // Training attendance
  if (typeof att.attendance_rate === 'number') {
    if (att.attendance_rate >= 90) out.push(`Excellent training attendance — ${att.attendance_rate}%.`);
    else if (att.attendance_rate >= 75) out.push(`Solid training attendance — ${att.attendance_rate}%.`);
  }

  // Fitness improvements
  const fa = ext.fitness_assessment;
  if (fa?.sessions_count) {
    const improved = (fa.key_changes || []).filter(k => k.delta > 0);
    if (improved.length) {
      out.push(`Improved on ${improved.length} fitness assessment test${improved.length === 1 ? '' : 's'}.`);
    }
  }

  // Biometrics
  const bm = ext.biometrics;
  if (bm?.entries_count) {
    const w = bm.deltas?.weight_kg;
    if (w !== null && w !== undefined) out.push(`Tracked physical development (weight ${w > 0 ? '+' : ''}${w} kg).`);
  }

  // Injury return
  const inj = ext.injury_recovery;
  if (inj?.injuries_count && inj.active_count < inj.injuries_count) {
    out.push(`Returned from injury — ${inj.injuries_count - inj.active_count} recovery completed.`);
  }

  // Academic
  const ac = ext.academic;
  if (ac?.attendance?.rate !== undefined && ac.attendance.rate !== null && ac.attendance.rate >= 85) {
    out.push(`Strong academic attendance — ${ac.attendance.rate}%.`);
  }
  if (ac?.latest_report?.average_grade) {
    out.push(`Latest academic average grade: ${ac.latest_report.average_grade}.`);
  }

  // Career pathway
  const cp = ext.career_pathway;
  if (cp?.completed_milestones?.length) {
    out.push(`Completed ${cp.completed_milestones.length} career-pathway milestone${cp.completed_milestones.length === 1 ? '' : 's'}.`);
  }

  // Mental engagement
  const m = ext.mental_hub;
  if (m?.completed_tasks) {
    out.push(`Engaged with mental development — ${m.completed_tasks} task${m.completed_tasks === 1 ? '' : 's'} completed.`);
  }

  // Cap at 10 to keep the section readable.
  return out.slice(0, 10);
}

// Exported map for the Builder UI.
export const SECTION_GENERATORS = {
  fitness:    (ext) => generateFitnessSummary(ext?.fitness_assessment),
  biometrics: (ext) => generateBiometricsSummary(ext?.biometrics),
  nutrition:  (ext) => generateNutritionSummary(ext?.nutrition),
  mental:     (ext) => generateMentalSummary(ext?.mental_hub),
  academic:   (ext) => generateAcademicSummary(ext?.academic),
  injury:     (ext) => generateInjurySummary(ext?.injury_recovery),
  career:     (ext) => generateCareerSummary(ext?.career_pathway),
};
